import { CallAttempt } from '../CallAttempt/CallAttempt';
import { getTaskScenarioMeta, TaskScenarioMeta } from './scenarioMapping';
import { TaskBase } from './Task';

export type DetailLoadState = 'loaded' | 'unsupported' | 'failed';

type NamedEntity = {
  id?: string | null;
  name?: string | null;
  title?: string | null;
  surname?: string | null;
  givenName?: string | null;
  patronym?: string | null;
};

type DetailTask = Omit<TaskBase, 'clinic' | 'doctor' | 'specialization'> & {
  end?: string | null;
  slotId?: string | null;
  slotSecondaryId?: string | null;
  batch?: NamedEntity & { from?: string | null };
  template?: NamedEntity;
  hisId?: string | null;
  clinic?: NamedEntity;
  doctor?: NamedEntity;
  specialization?: NamedEntity;
};

export interface CallAttemptDetailViewModel {
  id: string;
  callDateTime: string;
  beginCallDateTime: string;
  endCallDateTime: string;
  state: string | null;
  phone: string;
  recording: string | null;
  hasRecording: boolean;
  hasTranscription: boolean | null;
}

export interface CallDetailsViewModel {
  isSupported: boolean;
  detailLoadState: DetailLoadState;
  scenario: TaskScenarioMeta;
  header: {
    taskId: string;
    taskType?: string | null;
    state?: string | null;
    result?: string | null;
    createdDateTime?: string | null;
    callDateTime?: string | null;
  };
  patient: {
    hisPatientId?: string | null;
    phone?: string | null;
    surname?: string | null;
    givenName?: string | null;
    patronym?: string | null;
    fullName?: string | null;
    birthDate?: string | null;
    gender?: number | null;
    clinicName?: string | null;
  };
  appointment?: {
    begin?: string | null;
    end?: string | null;
    slotId?: string | null;
    slotSecondaryId?: string | null;
    clinicName?: string | null;
    doctorName?: string | null;
    specializationName?: string | null;
  };
  additional?: {
    paymentType?: string | null;
    pathogens?: string | null;
  };
  campaign?: {
    id?: string | null;
    name?: string | null;
    from?: string | null;
    clinicName?: string | null;
  };
  form?: {
    hisId?: string | null;
    templateId?: string | null;
    templateName?: string | null;
    batchId?: string | null;
    batchName?: string | null;
  };
  callAttempts: CallAttemptDetailViewModel[];
}

const compactName = (parts: Array<string | null | undefined>) => {
  const name = parts.filter(Boolean).join(' ').trim();
  return name || null;
};

const entityDisplayName = (entity?: NamedEntity) =>
  entity?.name ??
  entity?.title ??
  compactName([entity?.surname, entity?.givenName, entity?.patronym]);

const normalizeAttempt = (
  attempt: CallAttempt,
): CallAttemptDetailViewModel => ({
  id: attempt.id,
  callDateTime: attempt.callDateTime,
  beginCallDateTime: attempt.beginCallDateTime,
  endCallDateTime: attempt.endCallDateTime,
  state: attempt.callAttemptState ?? attempt.state ?? null,
  phone: attempt.phone,
  recording: attempt.recording,
  hasRecording: Boolean(attempt.recording),
  hasTranscription: null,
});

export const createTaskDetailFallbackViewModel = ({
  taskId,
  taskType,
  callAttempts = [],
  detailLoadState,
  isSupported,
  taskTypeLabel,
}: {
  taskId: string;
  taskType?: string | null;
  callAttempts?: CallAttempt[];
  detailLoadState: DetailLoadState;
  isSupported: boolean;
  taskTypeLabel?: string | null;
}): CallDetailsViewModel => ({
  isSupported,
  detailLoadState,
  scenario: getTaskScenarioMeta(taskType ?? '', taskTypeLabel),
  header: {
    taskId,
    taskType,
  },
  patient: {},
  callAttempts: callAttempts.map(normalizeAttempt),
});

export const normalizeTaskDetailViewModel = (
  task: DetailTask,
  fallbackCallAttempts: CallAttempt[] = [],
  taskTypeLabel?: string | null,
): CallDetailsViewModel => {
  const taskType = task.type ?? '';
  const scenario = getTaskScenarioMeta(taskType, taskTypeLabel);
  const callAttempts = task.callAttempts ?? fallbackCallAttempts;
  const viewModel: CallDetailsViewModel = {
    isSupported: scenario.group !== 'unknown',
    detailLoadState: scenario.group === 'unknown' ? 'unsupported' : 'loaded',
    scenario,
    header: {
      taskId: task.id,
      taskType,
      state: task.state ?? null,
      result: task.result ?? null,
      createdDateTime: task.createdDateTime,
      callDateTime: task.callDateTime,
    },
    patient: {
      hisPatientId: task.hisPatientId,
      phone: task.phone,
      surname: task.surname,
      givenName: task.givenName,
      patronym: task.patronym,
      fullName: compactName([task.surname, task.givenName, task.patronym]),
      birthDate: task.birthDate,
      gender: task.gender ?? null,
      clinicName: entityDisplayName(task.clinic),
    },
    callAttempts: callAttempts.map(normalizeAttempt),
  };

  if (scenario.group === 'slot') {
    viewModel.appointment = {
      begin: task.begin,
      end: task.end,
      slotId: task.slotId,
      slotSecondaryId: task.slotSecondaryId,
      clinicName: entityDisplayName(task.clinic),
      doctorName: entityDisplayName(task.doctor),
      specializationName: entityDisplayName(task.specialization),
    };

    const isPaid = task.additionalData?.isPaid;
    if (isPaid !== null && isPaid !== undefined) {
      viewModel.additional = {
        paymentType: isPaid ? 'Платно' : 'ОМС',
      };
    }
  }

  if (scenario.group === 'dispensarization') {
    viewModel.campaign = {
      id: task.batch?.id,
      name: task.batch?.name,
      from: task.batch?.from,
      clinicName: entityDisplayName(task.clinic),
    };
  }

  if (scenario.group === 'informing') {
    const pathogens = task.additionalData?.pathogens
      ?.map((pathogen) => pathogen.trim())
      .filter(Boolean)
      .join(', ');
    if (pathogens) {
      viewModel.additional = { pathogens };
    }
  }

  if (scenario.group === 'form') {
    viewModel.campaign = {
      id: task.batch?.id,
      name: task.batch?.name,
      from: task.batch?.from,
      clinicName: entityDisplayName(task.clinic),
    };
    viewModel.form = {
      hisId: task.hisId,
      templateId: task.template?.id,
      templateName: task.template?.name,
      batchId: task.batch?.id,
      batchName: task.batch?.name,
    };
  }

  return viewModel;
};
